/**
 * Chats: one conversation per person, the same for everyone (family, friends, the landlord, a church, an
 * agency, and later real players). You can always write; whether they answer, and how, comes from who they
 * are to you (closeness, what you asked) and where they're from. A message is the simulation reaching you;
 * your reply feeds back into it (closeness).
 */
import type { LifeState, Message } from './life.ts';
import { Rng } from './rng.ts';
import { homeCountry, type Country } from './travel.ts';

export interface Thread {
  /** who it's with (as messages name them: a first name, or 'Landlord', a church, an agency) */
  name: string;
  last: Message;
  unread: number;
  /** a person in your life (they can answer), or an organisation (they don't) */
  contact: LifeState['contacts'][number] | null;
  /** a real player (their id): they write their own replies */
  player?: string;
}

/** the person behind a thread name (messages use first names) */
export function contactFor(s: LifeState, name: string) {
  return s.contacts.find((c) => c.name === name) ?? s.contacts.find((c) => c.name.split(' ')[0] === name) ?? null;
}

const keyOf = (m: Message) => (m.mine ? m.with ?? '' : m.from);

/** Every conversation, newest first. */
export function threads(s: LifeState): Thread[] {
  const by = new Map<string, Thread>();
  for (const m of s.messages) { // (newest first already)
    const k = keyOf(m);
    if (!k) continue;
    const t = by.get(k);
    if (!t) by.set(k, { name: k, last: m, unread: !m.mine && !m.read ? 1 : 0, contact: contactFor(s, k) });
    else if (!m.mine && !m.read) t.unread++;
  }
  // people you haven't talked to yet can still be written to: they're in the list, at the bottom
  const out = [...by.values()];
  // players you've swapped numbers with: their threads say so (and are there before anyone's written)
  for (const p of s.players ?? []) { const t = by.get(p.name); if (t) { t.player = p.id; t.contact = null; } else out.push({ name: p.name, last: { t: -1, from: p.name, text: '', read: true }, unread: 0, contact: null, player: p.id }); }
  for (const c of s.contacts) {
    const first = c.name.split(' ')[0];
    if (!by.has(c.name) && !by.has(first)) out.push({ name: c.name, last: { t: -1, from: c.name, text: '', read: true }, unread: 0, contact: c });
  }
  return out;
}

/** One conversation, oldest first. */
export function conversation(s: LifeState, name: string): Message[] {
  return s.messages.filter((m) => keyOf(m) === name).reverse();
}

export function markRead(s: LifeState, name: string) {
  for (const m of s.messages) if (!m.mine && m.from === name) m.read = true;
}

type Intent = 'greet' | 'invite' | 'money' | 'thanks' | 'sorry' | 'love' | 'question' | 'other';
function intentOf(text: string): Intent {
  const t = text.toLowerCase();
  if (/\b(love you|miss you|❤|😘)/.test(t)) return 'love';
  if (/\b(sorry|apolog)/.test(t)) return 'sorry';
  if (/\b(thank|thanks|thx)/.test(t)) return 'thanks';
  if (/\b(lend|borrow|money|send me|cash|transfer)\b/.test(t)) return 'money';
  if (/\b(hang|come out|drink|meet|link up|weekend|party|dinner|lunch|movie)\b/.test(t)) return 'invite';
  if (/^(hi|hey|hello|yo|good (morning|evening|afternoon))\b/.test(t)) return 'greet';
  if (/\?\s*$/.test(t)) return 'question';
  return 'other';
}

/** how people answer, by where they're from (warm / cool) */
const REPLIES: Record<Country, Record<Intent, [string[], string[]]>> = {
  nigeria: {
    greet: [['Heyyy! How far?', 'Hello dear, how are you?', 'I dey o. You nko?'], ['Hi.', 'Hello.']],
    invite: [['I dey! Just tell me where.', 'Ah yes o, this weekend works.', 'Say the time, I go come.'], ['This weekend is tight, maybe next time.', 'I no fit this one, sorry.']],
    money: [['How much you need? Let me see what I can do.', 'E dey hard, but I fit manage small.'], ['Me sef I am broke this month.', 'Hmm. Not now, abeg.']],
    thanks: [['Anytime!', 'No wahala 🙏'], ['Ok.']],
    sorry: [['It\'s fine, don\'t worry.', 'We move. All is forgiven.'], ['Okay.', 'Hmm. We\'ll see.']],
    love: [['❤️❤️ Love you too', 'Awww 🥰'], ['Haha okay 😅']],
    question: [['Hmm, let me think. I\'ll call you.', 'Yes o!', 'I no sure, but I go find out.'], ['I don\'t know.', 'Not sure.']],
    other: [['😂😂', 'True talk.', 'Lol. You no well.', 'Ehen? Tell me more.'], ['Ok.', '👍']],
  },
  france: {
    greet: [['Coucou! Ça va?', 'Salut! How are you?'], ['Salut.', 'Bonjour.']],
    invite: [['Avec plaisir! When?', 'Yes! An apéro on Saturday?'], ['Pas ce week-end, désolé.', 'I can\'t, sorry.']],
    money: [['How much? I can help a little.'], ['Désolé, it\'s a tight month.', 'Hmm, not right now.']],
    thanks: [['De rien!', 'Avec plaisir 😊'], ['Ok.']],
    sorry: [['C\'est pas grave.', 'Don\'t worry about it.'], ['D\'accord.']],
    love: [['Moi aussi ❤️', 'Bisous 😘'], ['Haha 😅']],
    question: [['Bonne question. I\'ll tell you later.', 'Oui!'], ['Aucune idée.']],
    other: [['Mdr 😂', 'Grave.', 'Ah bon? Tell me more.'], ['Ok.', '👍']],
  },
  uae: {
    greet: [['Hey habibi! Kaifa haluk?', 'Marhaba! All good?'], ['Hi.', 'Salam.']],
    invite: [['Yalla! When?', 'Sure! Brunch on Friday?'], ['Not this weekend, sorry habibi.', 'I can’t, sorry.']],
    money: [['How much? I can help a little.'], ['Wallah, this month is tight.', 'Hmm, not right now.']],
    thanks: [['Afwan!', 'Anytime 😊'], ['Ok.']],
    sorry: [['Maalesh, no problem.', 'Don’t worry about it.'], ['Ok.']],
    love: [['Me too ❤️', 'Habibi 😘'], ['Haha 😅']],
    question: [['Good question. I’ll tell you later.', 'Inshallah!'], ['Wallah, I don’t know.']],
    other: [['Hahaha 😂', 'Yalla!', 'Seriously? Tell me more.'], ['Ok.', '👍']],
  },
  italy: {
    greet: [['Ciao! Come stai?', 'Ehi! Tutto bene?'], ['Ciao.', 'Buongiorno.']],
    invite: [['Volentieri! When?', 'Sì! An aperitivo on Saturday?'], ['Questo weekend no, mi dispiace.', 'I can\'t, sorry.']],
    money: [['How much? I can help a little.'], ['Mi dispiace, it\'s a tight month.', 'Hmm, not right now.']],
    thanks: [['Prego!', 'Figurati 😊'], ['Ok.']],
    sorry: [['Non fa niente.', 'Don\'t worry about it.'], ['Va bene.']],
    love: [['Anch\'io ❤️', 'Un bacio 😘'], ['Haha 😅']],
    question: [['Bella domanda. I\'ll tell you later.', 'Sì!'], ['Boh, non lo so.']],
    other: [['Ahahah 😂', 'Dai!', 'Davvero? Tell me more.'], ['Ok.', '👍']],
  },
  usa: {
    greet: [['Heyyy! How are you??', 'Hey you! What\'s up?'], ['Hey.', 'Hi.']],
    invite: [['I\'m so down. When?', 'Yes!! Saturday?'], ['Can\'t this weekend, rain check?', 'Ugh, I\'m slammed. Sorry.']],
    money: [['How much do you need? I can Venmo you a bit.'], ['Sorry, money\'s tight right now.', 'Can\'t, sorry.']],
    thanks: [['Of course!', 'Anytime 🙌'], ['k']],
    sorry: [['It\'s all good.', 'Don\'t worry about it.'], ['Okay.']],
    love: [['Love you too ❤️', 'Aww 🥹'], ['Haha 😅']],
    question: [['Hmm, good question. Let me check.', 'Yes!'], ['No idea.']],
    other: [['LOL 😂', 'Literally.', 'Wait, tell me more.'], ['k', '👍']],
  },
  china: {
    greet: [['Hey! 好久不见 (long time no see)!', 'Hi! How are you?'], ['Hi.', '嗯 (mm).']],
    invite: [['好啊 (sure)! When?', 'Yes! Hotpot on Saturday?'], ['This weekend I have to work overtime, sorry.', '下次吧 (next time).']],
    money: [['How much? I can transfer a little on WeChat.'], ['Sorry, I\'m short this month.', 'Not right now.']],
    thanks: [['不客气 (you\'re welcome)!', 'No problem 👍'], ['Ok.']],
    sorry: [['没事 (it\'s fine).', 'Don\'t worry.'], ['Ok.']],
    love: [['❤️❤️', '爱你 (love you) 🥰'], ['Haha 😅']],
    question: [['Let me check and tell you.', '对 (yes)!'], ['不知道 (no idea).']],
    other: [['哈哈哈 😂', 'Really?', 'Tell me more.'], ['Ok.', '👍']],
  },
  japan: {
    greet: [['Hey! 久しぶり (long time)!', 'Hi! How have you been?'], ['Hi.', 'Hello.']],
    invite: [['いいね (sounds good)! When?', 'Yes! Izakaya on Friday?'], ['Sorry, work is busy this week.', 'Maybe next time.']],
    money: [['How much? I can help a little.'], ['Sorry, it\'s difficult right now.']],
    thanks: [['どういたしまして (you\'re welcome)!', 'No problem 😊'], ['Ok.']],
    sorry: [['大丈夫 (it\'s okay).', 'Don\'t worry.'], ['Okay.']],
    love: [['❤️', 'Me too 🥰'], ['Haha 😅']],
    question: [['Let me check.', 'はい (yes)!'], ['I don\'t know.']],
    other: [['笑 (lol) 😂', 'Really?', 'Tell me more!'], ['Ok.', '👍']],
  },
};

/** Things you might say (tap to send), as well as typing your own. */
export function suggestions(s: LifeState, name: string): string[] {
  const c = contactFor(s, name);
  if (!c) return ['Thank you.', 'Okay, noted.', 'Is this still available?'];
  const fam = ['Mother', 'Father', 'Brother', 'Sister', 'Son', 'Daughter'].includes(c.relation);
  const partner = /Girlfriend|Boyfriend|Wife|Husband/.test(c.relation);
  return [
    'Hey! How are you?',
    fam ? 'I miss you. I\'ll visit soon.' : partner ? 'I miss you ❤️' : 'Let\'s hang out this weekend?',
    'Thanks for everything 🙏',
    'Sorry I\'ve been quiet.',
  ];
}

/**
 * Send a text. It always goes; someone in your life may answer (sooner and kinder the closer you are), an
 * organisation doesn't. Returns what happened, for the UI to say.
 */
/** A text to a real player: kept in the thread; the game sends it (their reply comes from them). */
export function sendPlayerText(s: LifeState, to: { id: string; name: string }, text: string, mid?: string): string | null {
  const body = text.trim().slice(0, 400);
  if (!body) return null;
  s.messages.unshift({ t: s.minutes, from: 'You', with: to.name, text: body, read: true, mine: true, player: to.id, ...(mid ? { mid } : {}) });
  return body;
}
/** A photo to a real player (the game has sent it: the picture is on the server, in both inboxes). */
export function sendPlayerPic(s: LifeState, to: { id: string; name: string }, id: string) {
  s.messages.unshift({ t: s.minutes, from: 'You', with: to.name, text: '📷 Photo', read: true, mine: true, player: to.id, pic: id, mid: id });
}
/** A photo from a real player. */
export function receivePlayerPic(s: LifeState, from: { id: string; name: string }, id: string) {
  s.messages.unshift({ t: s.minutes, from: from.name, text: '📷 Photo', read: false, player: from.id, pic: id, mid: id });
}
/** the reactions there are (a tap-and-hold on a message) */
export const REACTIONS = ['❤️', '😂', '😮', '😢', '🔥', '👍'] as const;
/** A reaction on a message with a real player (yours, or theirs as it arrived); the same one again takes yours off. */
export function reactPlayer(s: LifeState, player: string, mid: string, emoji: string, mine: boolean): boolean {
  const m = s.messages.find((x) => x.player === player && x.mid === mid);
  if (!m || !(REACTIONS as readonly string[]).includes(emoji)) return false;
  const r = (m.react ??= {});
  if (mine) r.me = r.me === emoji ? undefined : emoji; else r.them = emoji;
  return true;
}

/** A text from a real player, as it arrived. */
export function receivePlayerText(s: LifeState, from: { id: string; name: string }, text: string, mid?: string) {
  s.messages.unshift({ t: s.minutes, from: from.name, text: String(text).slice(0, 600), read: false, player: from.id, ...(mid && /^[a-z0-9]{6,40}$/i.test(mid) ? { mid } : {}) });
}

/** A voice note to a real player (the game has sent it: the audio is on the server). */
export function sendPlayerVoice(s: LifeState, to: { id: string; name: string }, id: string, secs: number) {
  s.messages.unshift({ t: s.minutes, from: 'You', with: to.name, text: '🎤 Voice note', read: true, mine: true, player: to.id, vn: { id, secs } });
}
/** A voice note from a real player. */
export function receivePlayerVoice(s: LifeState, from: { id: string; name: string }, id: string, secs: number) {
  s.messages.unshift({ t: s.minutes, from: from.name, text: '🎤 Voice note', read: false, player: from.id, vn: { id, secs } });
}

/** the most one transfer can be (in the game's units: ₦) */
export const TRANSFER_MAX = 50_000_000;
/** the most in one go through the bank */
export const BANK_MAX = 1_000_000_000_000;
/** Can you send this much to a player? null: yes; otherwise why not. */
export function transferProblem(s: LifeState, amount: number): string | null {
  if (!Number.isFinite(amount) || amount < 1) return 'Enter an amount.';
  if (amount > TRANSFER_MAX) return 'too much';
  if (amount > s.money) return 'short';
  return null;
}
/** Money to a real player (after the server took it): out of your account, in the thread and the ledger. */
export function sendPlayerMoney(s: LifeState, to: { id: string; name: string }, amount: number) {
  const n = Math.round(amount);
  s.money -= n;
  s.ledger.push({ t: s.minutes, amount: -n, label: `Sent to ${to.name}` });
  s.messages.unshift({ t: s.minutes, from: 'You', with: to.name, text: '💸 Money sent', read: true, mine: true, player: to.id, cash: n });
}
/** Money from a real player: into your account. */
export function receivePlayerMoney(s: LifeState, from: { id: string; name: string }, amount: number) {
  const n = Math.max(0, Math.min(BANK_MAX, Math.round(amount)));
  s.money += n;
  s.ledger.push({ t: s.minutes, amount: n, label: `From ${from.name}` });
  s.messages.unshift({ t: s.minutes, from: from.name, text: '💸 Money received', read: false, player: from.id, cash: n });
}

export function sendText(s: LifeState, to: string, text: string): { replying: boolean } {
  const body = text.trim().slice(0, 400);
  if (!body) return { replying: false };
  s.messages.unshift({ t: s.minutes, from: 'You', with: to, text: body, read: true, mine: true });
  const c = contactFor(s, to);
  if (!c) return { replying: false };
  const r = new Rng((s.seed ^ Math.imul(s.minutes + s.messages.length * 131, 2654435761)) >>> 0);
  const intent = intentOf(body);
  // writing to someone is a small kindness in itself (once a day counts)
  const today = Math.floor(s.minutes / 1440);
  const k = `${c.name}|${today}`;
  s.texted ??= [];
  if (!s.texted.includes(k)) { s.texted.push(k); if (s.texted.length > 60) s.texted.shift(); c.closeness = Math.min(100, c.closeness + (intent === 'sorry' || intent === 'love' ? 3 : 1)); }
  // do they answer? (and are they warm about it?)
  const answers = r.chance(Math.min(0.95, 0.35 + c.closeness / 120));
  if (!answers) return { replying: false };
  const warm = r.chance(Math.min(0.95, c.closeness / 90 + (intent === 'money' ? -0.25 : 0)));
  const lines = REPLIES[homeCountry(s)][intent][warm ? 0 : 1];
  s.inbox.push({ at: s.minutes + r.int(2, Math.max(4, 45 - Math.round(c.closeness / 3))), from: to, text: r.pick(lines) });
  return { replying: true };
}
